// Activation origin (T0), conditional entry-point import timing, prefetch wait
// reconciliation, and actual viewport-entry separation for Glyph Map (FONTS-21708 H7).

import { monotonicNowMs, reportGlyphMapOperational, reportSafely } from './analytics.js';
import type {
  GlyphAttemptTrigger,
  GlyphLoadTelemetryContext,
  GlyphSelectionTrigger
} from './glyphMapData.js';

export interface SectionImportTelemetrySpan {
  startedAtMs: number;
  settledAtMs: number | null;
  durationMs: number | null;
  outcome: 'pending' | 'success' | 'failure';
  reusedCount: number;
}

export const ACTIVATION_SELECTION_JOIN_TIMEOUT_MS = 1000;

let activeImportPromise: Promise<unknown> | null = null;
let currentImportSpan: SectionImportTelemetrySpan | null = null;

/**
 * @returns The current or most recent `<af-glyph-map-section>` dynamic import span.
 */
export function getSectionImportTelemetry(): SectionImportTelemetrySpan | null {
  return currentImportSpan ? { ...currentImportSpan } : null;
}

/**
 * Resets module-level entry-point import state between unit tests.
 */
export function resetSectionImportTelemetryForTests(): void {
  activeImportPromise = null;
  currentImportSpan = null;
}

export interface SectionImportTelemetryOptions {
  now?: () => number;
  report?: (record: Record<string, unknown>) => void;
}

/**
 * Loads the `<af-glyph-map-section>` module once and records its settlement span (H7).
 * Import failure leaves activation and selection identity unavailable (`null`), logs the
 * error, and reports it to New Relic Errors without throwing an unhandled rejection.
 * @param loader - Thunk performing the dynamic `import('../components/GlyphMapSection.ts')`.
 * @param options - Optional clock and reporting overrides for testing.
 * @returns The settled module or `null` when the import fails.
 */
export function loadGlyphMapSectionModule<T = unknown>(
  loader: () => Promise<T>,
  options: SectionImportTelemetryOptions = {}
): Promise<T | null> {
  const now = options.now ?? monotonicNowMs;
  const report = options.report ?? reportGlyphMapOperational;

  if (activeImportPromise && currentImportSpan) {
    currentImportSpan.reusedCount += 1;
    const reusedSpan = currentImportSpan;
    const reusedCount = reusedSpan.reusedCount;
    // Report after settlement so a reuse during a pending import carries its real outcome.
    return (activeImportPromise as Promise<T | null>).then(mod => {
      report({
        kind: 'section-import',
        stage: 'entry-import',
        outcome: reusedSpan.outcome,
        reused: true,
        reusedCount,
        durationMs: reusedSpan.durationMs ?? 0,
        activationOriginMs: null,
        styleLoadId: null
      });
      return mod;
    });
  }

  const startedAtMs = now();
  const span: SectionImportTelemetrySpan = {
    startedAtMs,
    settledAtMs: null,
    durationMs: null,
    outcome: 'pending',
    reusedCount: 0
  };
  currentImportSpan = span;

  const settleSpan = (outcome: 'success' | 'failure'): number => {
    const settledAtMs = now();
    const durationMs = Math.max(0, settledAtMs - startedAtMs);
    span.settledAtMs = settledAtMs;
    span.durationMs = durationMs;
    span.outcome = outcome;
    report({
      kind: 'section-import',
      stage: 'entry-import',
      outcome,
      reused: false,
      reusedCount: 0,
      durationMs,
      activationOriginMs: null,
      styleLoadId: null
    });
    return durationMs;
  };

  const promise = Promise.resolve()
    .then(() => loader())
    .then(
      mod => {
        settleSpan('success');
        return mod;
      },
      (error: unknown) => {
        settleSpan('failure');
        activeImportPromise = null;
        console.error('glyph map: failed to import GlyphMapSection', error);
        // Catching the rejection hides it from the agent's unhandled-rejection capture.
        reportSafely(() => {
          window.newrelic?.noticeError?.(error instanceof Error ? error : new Error(String(error)));
        });
        return null;
      }
    );

  activeImportPromise = promise;
  return promise;
}

export type PrefetchActivationStatus =
  | 'completed-before-activation'
  | 'failed-before-activation'
  | 'pending-at-activation'
  | 'not-started';

export interface GlyphMapActivationSnapshot {
  familySlug: string;
  initialStyleId: string;
  activationOriginMs: number | null;
  viewportEntryMs: number | null;
  activationToViewportEntryMs: number | null;
  initialStyleLoadId: string | null;
  selectionTrigger: GlyphSelectionTrigger;
  attemptTrigger: GlyphAttemptTrigger;
  prefetchStatusAtActivation: PrefetchActivationStatus;
  prefetchTotalDurationMs: number | null;
  remainingPrefetchWaitMs: number | null;
  sectionImportDurationMs: number | null;
}

export interface GlyphMapActivationTrackerOptions {
  familySlug: string;
  initialStyleId: string;
  now?: () => number;
  isSuppressed?: () => boolean;
  report?: (record: Record<string, unknown>) => void;
}

/**
 * Tracks a `<af-glyph-map-section>` instance's activation origin (T0), prefetch
 * completion relative to T0, initial style-selection correlation, and actual
 * 0px viewport entry separately from the 200px activation margin (H7).
 */
export class GlyphMapActivationTracker {
  private readonly familySlug: string;
  private readonly initialStyleId: string;
  private readonly now: () => number;
  private readonly isSuppressed?: () => boolean;
  private readonly report: (record: Record<string, unknown>) => void;

  private prefetchStartedAtMs: number | null = null;
  private prefetchSettledAtMs: number | null = null;
  private prefetchOutcome: 'not-started' | 'pending' | 'completed' | 'failed' = 'not-started';

  private activationOriginMs: number | null = null;
  private prefetchStatusAtActivation: PrefetchActivationStatus = 'not-started';
  private viewportEntryMs: number | null = null;

  private initialStyleLoadId: string | null = null;
  private initialSelectionTrigger: GlyphSelectionTrigger = 'initial-auto';
  private initialAttemptTrigger: GlyphAttemptTrigger = 'initial';
  private activationEmitted = false;
  private viewportEmitted = false;

  /**
   * @param options - Section identity and optional clock/reporting hooks.
   */
  constructor(options: GlyphMapActivationTrackerOptions) {
    this.familySlug = options.familySlug;
    this.initialStyleId = options.initialStyleId;
    this.now = options.now ?? monotonicNowMs;
    this.isSuppressed = options.isSuppressed;
    this.report = options.report ?? reportGlyphMapOperational;
  }

  /**
   * Records the start of `prefetchCoverage` before activation observer registration.
   */
  recordPrefetchStart(): void {
    // A reconnect retries a failed prefetch (failed coverage is not cached) before activation.
    const retryingFailedPrefetch =
      this.prefetchOutcome === 'failed' && this.activationOriginMs === null;
    if (this.prefetchStartedAtMs !== null && !retryingFailedPrefetch) {
      return;
    }
    this.prefetchStartedAtMs = this.now();
    this.prefetchSettledAtMs = null;
    this.prefetchOutcome = 'pending';
  }

  /**
   * Records settlement of `prefetchCoverage`.
   * @param outcome - Whether prefetch resolved or rejected.
   */
  recordPrefetchSettled(outcome: 'completed' | 'failed'): void {
    if (this.prefetchSettledAtMs === null) {
      this.prefetchSettledAtMs = this.now();
    }
    this.prefetchOutcome = outcome;
    if (this.activationOriginMs !== null && !this.activationEmitted) {
      this.emitOrScheduleActivationOrigin();
    }
  }

  /**
   * Captures callback-entry T0 for the first qualifying live activation before `mountGlyphMap`.
   * Subsequent calls are ignored so a section has at most one activation origin.
   * @returns The captured T0 timestamp in milliseconds.
   */
  recordActivationOrigin(): number {
    if (this.activationOriginMs !== null) {
      return this.activationOriginMs;
    }
    const t0 = this.now();
    this.activationOriginMs = t0;
    if (this.prefetchOutcome === 'completed') {
      this.prefetchStatusAtActivation = 'completed-before-activation';
    } else if (this.prefetchOutcome === 'failed') {
      this.prefetchStatusAtActivation = 'failed-before-activation';
    } else if (this.prefetchOutcome === 'pending') {
      this.prefetchStatusAtActivation = 'pending-at-activation';
    } else {
      this.prefetchStatusAtActivation = 'not-started';
    }
    if (this.prefetchOutcome !== 'pending') {
      this.emitOrScheduleActivationOrigin();
    }
    return t0;
  }

  /**
   * Retries a settled `activation-origin` record that was suppressed while the section was detached.
   */
  replayPendingActivationOrigin(): void {
    if (
      this.activationOriginMs !== null &&
      this.prefetchOutcome !== 'pending' &&
      !this.activationEmitted
    ) {
      this.emitOrScheduleActivationOrigin();
    }
  }

  private emitOrScheduleActivationOrigin(): void {
    if (this.initialStyleLoadId !== null) {
      this.emitActivationOrigin();
      return;
    }
    // The Site starts its initial selection asynchronously after mount, so
    // joinSelection normally emits; this bounded fallback still reports T0 without one.
    // The timer is left pending because emitActivationOrigin is once-only and suppressed while disconnected.
    setTimeout(() => {
      if (this.activationOriginMs !== null && this.prefetchOutcome !== 'pending') {
        this.emitActivationOrigin();
      }
    }, ACTIVATION_SELECTION_JOIN_TIMEOUT_MS);
  }

  /**
   * Joins the initial style selection's `styleLoadId` with T0.
   * Later style selections (`selectionTrigger === 'style-change'`) keep their own timeline.
   * @param telemetry - Telemetry context from `loadCoverage` or `loadPlannedFontFace`.
   */
  joinSelection(telemetry?: GlyphLoadTelemetryContext): void {
    if (!telemetry) {
      return;
    }
    if (telemetry.selectionTrigger === 'style-change') {
      return;
    }
    if (this.initialStyleLoadId === null) {
      this.initialStyleLoadId = telemetry.styleLoadId;
      this.initialSelectionTrigger = telemetry.selectionTrigger;
      this.initialAttemptTrigger = telemetry.attemptTrigger;
      if (
        this.activationOriginMs !== null &&
        this.prefetchOutcome !== 'pending' &&
        !this.activationEmitted
      ) {
        this.emitActivationOrigin();
      }
      if (this.viewportEntryMs !== null) {
        this.emitViewportEntry();
      }
    }
  }

  /**
   * Records actual 0px viewport entry separately from the 200px activation margin.
   * @returns The captured viewport-entry timestamp.
   */
  recordViewportEntry(): number {
    if (this.viewportEntryMs !== null) {
      return this.viewportEntryMs;
    }
    const enteredAt = this.now();
    this.viewportEntryMs = enteredAt;
    this.emitOrScheduleViewportEntry();
    return enteredAt;
  }

  private emitOrScheduleViewportEntry(): void {
    if (this.initialStyleLoadId !== null) {
      this.emitViewportEntry();
      return;
    }
    // A section already in view can enter before the initial selection starts; wait for its styleLoadId like activation-origin.
    setTimeout(() => this.emitViewportEntry(), ACTIVATION_SELECTION_JOIN_TIMEOUT_MS);
  }

  /**
   * Retries a captured `viewport-entry` record that was suppressed while the section was detached.
   */
  replayPendingViewportEntry(): void {
    if (this.viewportEntryMs !== null && !this.viewportEmitted) {
      this.emitOrScheduleViewportEntry();
    }
  }

  private emitViewportEntry(): void {
    if (this.viewportEmitted || this.viewportEntryMs === null || this.isSuppressed?.()) {
      return;
    }
    this.viewportEmitted = true;
    const snapshot = this.getSnapshot();
    this.report({
      kind: 'viewport-entry',
      stage: 'viewport-observation',
      outcome: 'entered',
      familySlug: this.familySlug,
      styleId: this.initialStyleId,
      styleLoadId: snapshot.initialStyleLoadId,
      selectionTrigger: snapshot.selectionTrigger,
      activationOriginMs: snapshot.activationOriginMs,
      viewportEntryMs: snapshot.viewportEntryMs,
      activationToViewportEntryMs: snapshot.activationToViewportEntryMs
    });
  }

  private computeRemainingPrefetchWaitMs(): number | null {
    if (this.activationOriginMs === null) {
      return null;
    }
    if (
      this.prefetchStatusAtActivation === 'completed-before-activation' ||
      this.prefetchStatusAtActivation === 'failed-before-activation' ||
      this.prefetchStatusAtActivation === 'not-started'
    ) {
      return 0;
    }
    if (this.prefetchSettledAtMs === null) {
      return null;
    }
    return Math.max(0, this.prefetchSettledAtMs - this.activationOriginMs);
  }

  // T0 is recorded by the imported module's observer, so the import always settles before T0.
  private computeSectionImportDurationMs(): number | null {
    const span = getSectionImportTelemetry();
    return span && span.settledAtMs !== null ? span.durationMs : null;
  }

  /**
   * Returns the current activation-origin and viewport-entry snapshot (exposed on
   * `GlyphMapSource.activationTelemetry` so Site S13 and tests can inspect T0).
   * @returns Snapshot of activation, prefetch, import, and viewport timings.
   */
  getSnapshot(): GlyphMapActivationSnapshot {
    // A pre-activation retry restarts the span, so this covers only the prefetch activation waited on.
    const prefetchTotalDurationMs =
      this.prefetchStartedAtMs !== null && this.prefetchSettledAtMs !== null
        ? Math.max(0, this.prefetchSettledAtMs - this.prefetchStartedAtMs)
        : null;
    const activationToViewportEntryMs =
      this.activationOriginMs !== null && this.viewportEntryMs !== null
        ? Math.max(0, this.viewportEntryMs - this.activationOriginMs)
        : null;
    return {
      familySlug: this.familySlug,
      initialStyleId: this.initialStyleId,
      activationOriginMs: this.activationOriginMs,
      viewportEntryMs: this.viewportEntryMs,
      activationToViewportEntryMs,
      initialStyleLoadId: this.initialStyleLoadId,
      selectionTrigger: this.initialSelectionTrigger,
      attemptTrigger: this.initialAttemptTrigger,
      prefetchStatusAtActivation: this.prefetchStatusAtActivation,
      prefetchTotalDurationMs,
      remainingPrefetchWaitMs: this.computeRemainingPrefetchWaitMs(),
      sectionImportDurationMs: this.computeSectionImportDurationMs()
    };
  }

  /**
   * Emits the settled `activation-origin` operational record (at most once per section).
   */
  emitActivationOrigin(): void {
    if (this.activationEmitted || this.activationOriginMs === null || this.isSuppressed?.()) {
      return;
    }
    this.activationEmitted = true;
    const snapshot = this.getSnapshot();
    this.report({
      kind: 'activation-origin',
      stage: 'section-activation',
      outcome:
        this.prefetchOutcome === 'failed' ? 'prefetch-failed' : 'activated',
      familySlug: snapshot.familySlug,
      styleId: snapshot.initialStyleId,
      styleLoadId: snapshot.initialStyleLoadId,
      selectionTrigger: snapshot.selectionTrigger,
      attemptTrigger: snapshot.attemptTrigger,
      activationOriginMs: snapshot.activationOriginMs,
      viewportEntryMs: snapshot.viewportEntryMs,
      activationToViewportEntryMs: snapshot.activationToViewportEntryMs,
      prefetchStatusAtActivation: snapshot.prefetchStatusAtActivation,
      prefetchTotalDurationMs: snapshot.prefetchTotalDurationMs,
      remainingPrefetchWaitMs: snapshot.remainingPrefetchWaitMs,
      sectionImportDurationMs: snapshot.sectionImportDurationMs
    });
  }
}
